import React, { forwardRef, Fragment, RefObject, useEffect, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

import {
  ProductsGlobalFilter,
  PlanSubscriptionGlobalFilter,
  CustomersGlobalFilter,
  OrderGlobalFilter,
  ContactsGlobalFilter,
  CompaniesGlobalFilter,
  LeadsGlobalFilter,
  CryptoOrdersGlobalFilter,
  InvoiceListGlobalSearch,
  TicketsListGlobalFilter,
  NFTRankingGlobalFilter,
  TaskListGlobalFilter,
} from "../../Components/Common/GlobalSearchFilter";

// Column Filter
const Filter = ({
  column
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? '') as string}
        onChange={value => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input {...props} value={value} id="search-bar-0" className="form-control search" onChange={e => setValue(e.target.value)} />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  isGlobalFilter?: any;
  isProductsFilter?: any;
  isPlanSubscriptionFilter?: any;
  isCustomerFilter?: any;
  isOrderFilter?: any;
  isContactsFilter?: any;
  isCompaniesFilter?: any;
  isLeadsFilter?: any;
  iscustomPageSize?: any;
  isCryptoOrdersFilter?: any;
  isInvoiceListFilter?: any;
  isTicketsListFilter?: any;
  isNFTRankingFilter?: any;
  isTaskListFilter?: any;
  handleTaskClick?: any;
  customPageSize?: any;
  tableClass?: any;
  theadClass?: any;
  tfootClass?: any;
  trClass?: any;
  thClass?: any;
  divClass?: any;
  SearchPlaceholder?: any;
  handleLeadClick?: any;
  handleCompanyClick?: any;
  handleContactClick?: any;
  handleTicketClick?: any;
  // Server-side pagination props
  manualPagination?: boolean;
  onPaginationChange?: (updater: any) => void;
  rowCount?: number;
  pageCount?: number;
  paginationState?: { pageIndex: number; pageSize: number };
}

const TableContainer = forwardRef<HTMLTableElement, TableContainerProps>(({
  columns,
  data,
  isGlobalFilter,
  isProductsFilter,
  isPlanSubscriptionFilter,
  isCustomerFilter,
  isOrderFilter,
  isContactsFilter,
  isCompaniesFilter,
  isLeadsFilter,
  isCryptoOrdersFilter,
  isInvoiceListFilter,
  isTicketsListFilter,
  isNFTRankingFilter,
  isTaskListFilter,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder,
  // Server-side pagination props
  manualPagination = false,
  onPaginationChange,
  rowCount,
  pageCount,
  paginationState,

}, ref) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank
    });
    return itemRank.passed;
  };

  const [internalPagination, setInternalPagination] = useState({
    pageIndex: 0,
    pageSize: customPageSize || 10,
  });

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
      pagination: manualPagination ? (paginationState || internalPagination) : internalPagination,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination,
    rowCount: manualPagination ? rowCount : undefined,
    pageCount: manualPagination ? pageCount : undefined,
    onPaginationChange: (updater: any) => {
      const prevState = manualPagination ? (paginationState || internalPagination) : internalPagination;
      const nextState = typeof updater === 'function' ? updater(prevState) : updater;
      
      // Ensure nextState is always an object and preserves pageSize
      const finalState = typeof nextState === 'number' 
        ? { ...prevState, pageIndex: nextState } 
        : { ...prevState, ...nextState };

      if (manualPagination && onPaginationChange) {
        onPaginationChange(finalState);
      } else {
        setInternalPagination(finalState);
      }
    },
    autoResetPageIndex: false,
  });
  const { pageIndex, pageSize } = table.getState().pagination;
  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    setPageSize,
    getState
  } = table;

  // Initial page size is handled by internalPagination state
  // and manually changing it via dropdown is now persistent.

  // Use rowCount for total if manualPagination is true, otherwise use data.length
  const totalResults = manualPagination ? (rowCount || 0) : data.length;

  return (
    <Fragment>
      {isGlobalFilter && <Row className="mb-3">
        <CardBody className="border border-dashed border-end-0 border-start-0">
          <form>
            <Row>
              <Col sm={5}>
                <div className={(isProductsFilter || isPlanSubscriptionFilter || isContactsFilter || isCompaniesFilter || isNFTRankingFilter) ? "search-box me-2 mb-2 d-inline-block" : "search-box me-2 mb-2 d-inline-block col-12"}>
                  <DebouncedInput
                    value={globalFilter ?? ''}
                    onChange={value => setGlobalFilter(String(value))}
                    placeholder={SearchPlaceholder}
                  />
                  <i className="bx bx-search-alt search-icon"></i>
                </div>
              </Col>
              {isProductsFilter && (
                <ProductsGlobalFilter />
              )}
              {isPlanSubscriptionFilter && (
                <PlanSubscriptionGlobalFilter />
              )}
              {isCustomerFilter && (
                <CustomersGlobalFilter />
              )}
              {isOrderFilter && (
                <OrderGlobalFilter />
              )}
              {isContactsFilter && (
                <ContactsGlobalFilter />
              )}
              {isCompaniesFilter && (
                <CompaniesGlobalFilter />
              )}
              {isLeadsFilter && (
                <LeadsGlobalFilter />
              )}
              {isCryptoOrdersFilter && (
                <CryptoOrdersGlobalFilter />
              )}
              {isInvoiceListFilter && (
                <InvoiceListGlobalSearch />
              )}
              {isTicketsListFilter && (
                <TicketsListGlobalFilter />
              )}
              {isNFTRankingFilter && (
                <NFTRankingGlobalFilter />
              )}
              {isTaskListFilter && (
                <TaskListGlobalFilter />
              )}
            </Row>
          </form>
        </CardBody>
      </Row>}


      <div className={divClass} ref={ref} >
        <Table hover className={tableClass} >
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th key={header.id} className={thClass}  {...{
                    onClick: header.column.getToggleSortingHandler(),
                  }}>
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' ',
                          desc: ' ',
                        }
                        [header.column.getIsSorted() as string] ?? null}
                        {header.column.getCanFilter() ? (
                          <div>
                            {/* <Filter column={header.column} table={table} /> */}
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.map((row: any) => {
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell: any) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
        {/* Page Info */}
        <div className="col-sm">
          <div className="text-muted">Displaying
            <span className="fw-semibold ms-1">
              {Math.min(totalResults,
                getState().pagination.pageSize * getState().pagination.pageIndex + 1,
              )} - {Math.min(totalResults,
                getState().pagination.pageSize * (getState().pagination.pageIndex + 1)
              )}
            </span> of
            <span className="fw-semibold"> {totalResults}</span> results
          </div>
        </div>

        {/* Page Size Dropdown */}
        <div className="col-sm-auto">
          <select
            className="form-select form-select-sm w-auto"
            value={getState().pagination.pageSize}
            onChange={(e) => {
              const value = e.target.value === "all" ? totalResults : Number(e.target.value);
              setPageSize(value);
            }}
          >
            <option value="all">All</option>
            {[5, 10, 25, 50, 75, 100, 200, 250, 400, 500, 750, 1000].map(size => (
              <option key={size} value={size}>{size} per page</option>
            ))}
          </select>
        </div>

        {/* Pagination Buttons */}
        <div className="col-sm-auto">
          <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">

            {/* First */}
            <li className={!getCanPreviousPage() ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); setPageIndex(0); }}>First</Link>
            </li>

            {/* Previous */}
            <li className={!getCanPreviousPage() ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); previousPage(); }}>Previous</Link>
            </li>

            {/* Dynamic Page Numbers */}
            {(() => {
              const pageIndex = getState().pagination.pageIndex;
              const totalPages = getPageOptions().length;
              const visiblePages = 10;
              const startPage = Math.floor(pageIndex / visiblePages) * visiblePages;
              const endPage = Math.min(startPage + visiblePages, totalPages);

              return getPageOptions()
                .slice(startPage, endPage)
                .map((item: number, key: number) => (
                  <li className={pageIndex === item ? "page-item active" : "page-item"} key={key}>
                    <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); setPageIndex(item); }}>
                      {item + 1}
                    </Link>
                  </li>
                ));
            })()}

            {/* Next */}
            <li className={!getCanNextPage() ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); nextPage(); }}>Next</Link>
            </li>

            {/* Last */}
            <li className={!getCanNextPage() ? "page-item disabled" : "page-item"}>
              <Link to="#" className="page-link" onClick={(e) => { e.preventDefault(); setPageIndex(getPageOptions().length - 1); }}>Last</Link>
            </li>
          </ul>
        </div>
      </Row>
    </Fragment>
  );
});

export default TableContainer;